import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import agentApiService from '../../services/agentApiService';

const isValidValue = (v) => {
  if (v === null || v === undefined) return false;
  const s = String(v).trim();
  return s.length > 0 && s !== 'N/A' && s !== '-' && s !== 'null' && s !== 'undefined';
};

const getCommentText = (c) => c?.comment || c?.content || c?.text || c?.message || '';
const getCommentAuthor = (c) => c?.supportName || c?.author || c?.userName || c?.sender || 'Support';
const getCommentDate = (c) => c?.cratedDate || c?.createdDate || c?.timestamp || c?.createdAt || '';

const SupportTicketDetail = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [copiedField, setCopiedField] = useState('');

  useEffect(() => {
    if (ticketId) {
      fetchTicketDetails();
    }
  }, [ticketId]);

  const fetchTicketDetails = async () => {
    try {
      setLoading(true);
      const response = await agentApiService.getSupportTicketDetails(ticketId);
      if (response.success) {
        setTicket(response.ticket);
      } else {
        setError(response.message || 'Failed to fetch ticket details');
        toast.error(response.message || 'Failed to fetch ticket details');
      }
    } catch (err) {
      console.error('❌ [SupportTicketDetail] Error:', err);
      setError(err.message);
      toast.error('Failed to fetch ticket details: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(label);
    toast.info(`Copied ${label} to clipboard!`);
    setTimeout(() => setCopiedField(''), 2000);
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) {
      toast.error('Please enter a reply');
      return;
    }

    try {
      setAddingComment(true);
      const response = await agentApiService.addCommentToTicket(ticketId, { comment: newComment.trim() });
      if (response.success) {
        toast.success('Reply sent successfully!');
        setNewComment('');
        await fetchTicketDetails();
      } else {
        toast.error(response.message || 'Failed to add comment');
      }
    } catch (err) {
      console.error('❌ [SupportTicketDetail] Error adding comment:', err);
      toast.error('Failed to add comment: ' + err.message);
    } finally {
      setAddingComment(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      OPEN: { bg: 'bg-orange-100 text-orange-800 border-orange-200', icon: 'fa-envelope-open', text: 'Open' },
      ASSIGNED: { bg: 'bg-blue-100 text-blue-800 border-blue-200', icon: 'fa-user-check', text: 'Assigned' },
      INPROGRESS: { bg: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: 'fa-spinner fa-spin', text: 'In Progress' },
      IN_PROGRESS: { bg: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: 'fa-spinner fa-spin', text: 'In Progress' },
      RESOLVE: { bg: 'bg-green-100 text-green-800 border-green-200', icon: 'fa-check-circle', text: 'Resolved' },
      CLOSED: { bg: 'bg-gray-100 text-gray-800 border-gray-200', icon: 'fa-lock', text: 'Closed' }
    };

    const config = statusConfig[status?.toUpperCase()] || {
      bg: 'bg-gray-100 text-gray-800 border-gray-200',
      icon: 'fa-info-circle',
      text: status || 'Unknown'
    };

    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${config.bg}`}>
        <i className={`fas ${config.icon}`}></i>
        {config.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!isValidValue(dateString)) return null;
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return String(dateString);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
        <p className="text-sm text-gray-500 font-medium">Loading ticket details...</p>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4 text-red-500">
            <i className="fas fa-exclamation-circle text-2xl"></i>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Ticket Not Found</h3>
          <p className="text-gray-600 mb-6 text-sm">{error || 'The requested ticket could not be found.'}</p>
          <Link
            to="/agent/dashboard/supportticket"
            className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm transition-colors w-full"
          >
            <i className="fas fa-arrow-left mr-2"></i> Back to Support Tickets
          </Link>
        </div>
      </div>
    );
  }

  const agentFullName = [ticket.agentName, ticket.agentLastName].filter(isValidValue).join(' ');
  const hasRelatedIds = isValidValue(ticket.bookingId) || isValidValue(ticket.paymentId) || isValidValue(ticket.refundId) || isValidValue(ticket.cancellationId);
  const isResolved = ticket.ticketStatus?.toUpperCase() === 'RESOLVE' || ticket.ticketStatus?.toUpperCase() === 'CLOSED';
  const commentsList = Array.isArray(ticket.comments) ? ticket.comments : [];

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="container mx-auto px-4 max-w-7xl">

        {/* ─── Top Navigation & Header Bar ───────────────────────────────── */}
        <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            
            {/* Left: Title & Ticket ID info */}
            <div className="flex items-start sm:items-center gap-3">
              <button
                onClick={() => navigate('/agent/dashboard/supportticket')}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Back to Support Tickets"
              >
                <i className="fas fa-arrow-left text-lg"></i>
              </button>
              <div>
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h1 className="text-xl font-bold text-gray-900">Ticket #{ticket.ticketId || ticketId}</h1>
                  {getStatusBadge(ticket.ticketStatus)}
                  {isValidValue(ticket.ticketId) && (
                    <button
                      onClick={() => handleCopy(ticket.ticketId, 'Ticket ID')}
                      className="text-xs text-gray-400 hover:text-gray-600 p-1"
                      title="Copy Ticket ID"
                    >
                      <i className={`fas ${copiedField === 'Ticket ID' ? 'fa-check text-green-600' : 'fa-copy'}`}></i>
                    </button>
                  )}
                </div>
                {isValidValue(ticket.createdDate) && (
                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1.5">
                    <i className="far fa-calendar-alt"></i>
                    Created on {formatDate(ticket.createdDate)}
                  </p>
                )}
              </div>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2.5">
              <button
                onClick={() => navigate('/agent/dashboard/supportticket')}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-medium transition-colors"
              >
                Back to Tickets
              </button>
            </div>

          </div>
        </div>

        {/* ─── Main Content Grid ─────────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* ─── Left 2 Columns: Ticket Details & Metadata ─────────────────── */}
          <div className="lg:col-span-2 space-y-6">

            {/* 1. Agent Query Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between pb-3 mb-4 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                    <i className="fas fa-question-circle text-base"></i>
                  </div>
                  <h2 className="text-base font-bold text-gray-900">Your Query Details</h2>
                </div>
                {isValidValue(ticket.agentQueryType || ticket.queryType) && (
                  <span className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium border border-blue-100">
                    Type: {ticket.agentQueryType || ticket.queryType}
                  </span>
                )}
              </div>
              <div className="bg-gradient-to-r from-gray-50 to-blue-50/20 rounded-xl p-4 border border-gray-100 text-gray-800 text-sm leading-relaxed whitespace-pre-wrap">
                {isValidValue(ticket.agentQuery) ? ticket.agentQuery : 'No query description provided.'}
              </div>
            </div>

            {/* 2. Agent Information Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-2 pb-3 mb-4 border-b border-gray-100">
                <div className="p-2 bg-emerald-50 rounded-lg text-emerald-600">
                  <i className="fas fa-user-circle text-base"></i>
                </div>
                <h2 className="text-base font-bold text-gray-900">Agent Details</h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {isValidValue(agentFullName) && (
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block">Agent Name</span>
                    <span className="text-sm font-semibold text-gray-900 mt-0.5 block">{agentFullName}</span>
                  </div>
                )}

                {isValidValue(ticket.agentEmail) && (
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block">Email Address</span>
                    <span className="text-sm text-gray-900 mt-0.5 block">{ticket.agentEmail}</span>
                  </div>
                )}

                {isValidValue(ticket.agentId) && (
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block">Agent ID</span>
                    <span className="text-sm font-mono font-medium text-gray-800 mt-0.5 block">{ticket.agentId}</span>
                  </div>
                )}

                {isValidValue(ticket.agentPhoneNo || ticket.phoneNumber) && (
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block">Phone Number</span>
                    <span className="text-sm text-gray-800 mt-0.5 block">{ticket.agentPhoneNo || ticket.phoneNumber}</span>
                  </div>
                )}
              </div>
            </div>

            {/* 3. Ticket Assignment & Lifecycle Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-2 pb-3 mb-4 border-b border-gray-100">
                <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
                  <i className="fas fa-info-circle text-base"></i>
                </div>
                <h2 className="text-base font-bold text-gray-900">Ticket Status & Support Assignment</h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block">Current Status</span>
                  <div className="mt-1">{getStatusBadge(ticket.ticketStatus)}</div>
                </div>

                {isValidValue(ticket.assignedTo) && ticket.assignedTo !== 'ADMIN_SELF' && (
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block">Support Executive Assigned</span>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                        <i className="fas fa-headset text-[10px]"></i>
                      </div>
                      <span className="text-sm font-semibold text-gray-900">
                        {isValidValue(ticket.assignedToName) ? ticket.assignedToName : ticket.assignedTo}
                      </span>
                    </div>
                  </div>
                )}

                {ticket.assignedTo === 'ADMIN_SELF' && (
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block">Support Assigned</span>
                    <span className="text-sm font-semibold text-indigo-700 mt-1 block">
                      <i className="fas fa-user-shield mr-1"></i> Admin Team
                    </span>
                  </div>
                )}

                {isResolved && (
                  <>
                    {isValidValue(ticket.resolveDate) && (
                      <div>
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block">Resolved On</span>
                        <span className="text-sm font-medium text-green-700 mt-1 block">
                          <i className="fas fa-calendar-check mr-1"></i> {formatDate(ticket.resolveDate)}
                        </span>
                      </div>
                    )}
                    {isValidValue(ticket.ticketResolveByName || ticket.ticketResolvedBy) && (
                      <div>
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block">Resolved By</span>
                        <span className="text-sm font-medium text-gray-900 mt-1 block">
                          {ticket.ticketResolveByName || ticket.ticketResolvedBy}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* 4. Related Reference IDs Card (ONLY displayed if at least one ID exists) */}
            {hasRelatedIds && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-2 pb-3 mb-4 border-b border-gray-100">
                  <div className="p-2 bg-amber-50 rounded-lg text-amber-600">
                    <i className="fas fa-link text-base"></i>
                  </div>
                  <h2 className="text-base font-bold text-gray-900">Related Booking & Transaction IDs</h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {isValidValue(ticket.bookingId) && (
                    <div className="p-3 bg-gray-50 rounded-lg flex items-center justify-between border border-gray-100">
                      <div>
                        <span className="text-xs text-gray-500 font-medium block">Booking ID</span>
                        <span className="text-sm font-mono font-semibold text-gray-900">{ticket.bookingId}</span>
                      </div>
                      <button
                        onClick={() => handleCopy(ticket.bookingId, 'Booking ID')}
                        className="text-gray-400 hover:text-gray-600 p-1.5 rounded hover:bg-white transition-colors"
                        title="Copy Booking ID"
                      >
                        <i className={`fas ${copiedField === 'Booking ID' ? 'fa-check text-green-600' : 'fa-copy'}`}></i>
                      </button>
                    </div>
                  )}

                  {isValidValue(ticket.paymentId) && (
                    <div className="p-3 bg-gray-50 rounded-lg flex items-center justify-between border border-gray-100">
                      <div>
                        <span className="text-xs text-gray-500 font-medium block">Payment ID</span>
                        <span className="text-sm font-mono font-semibold text-gray-900">{ticket.paymentId}</span>
                      </div>
                      <button
                        onClick={() => handleCopy(ticket.paymentId, 'Payment ID')}
                        className="text-gray-400 hover:text-gray-600 p-1.5 rounded hover:bg-white transition-colors"
                        title="Copy Payment ID"
                      >
                        <i className={`fas ${copiedField === 'Payment ID' ? 'fa-check text-green-600' : 'fa-copy'}`}></i>
                      </button>
                    </div>
                  )}

                  {isValidValue(ticket.refundId) && (
                    <div className="p-3 bg-gray-50 rounded-lg flex items-center justify-between border border-gray-100">
                      <div>
                        <span className="text-xs text-gray-500 font-medium block">Refund ID</span>
                        <span className="text-sm font-mono font-semibold text-gray-900">{ticket.refundId}</span>
                      </div>
                      <button
                        onClick={() => handleCopy(ticket.refundId, 'Refund ID')}
                        className="text-gray-400 hover:text-gray-600 p-1.5 rounded hover:bg-white transition-colors"
                        title="Copy Refund ID"
                      >
                        <i className={`fas ${copiedField === 'Refund ID' ? 'fa-check text-green-600' : 'fa-copy'}`}></i>
                      </button>
                    </div>
                  )}

                  {isValidValue(ticket.cancellationId) && (
                    <div className="p-3 bg-gray-50 rounded-lg flex items-center justify-between border border-gray-100">
                      <div>
                        <span className="text-xs text-gray-500 font-medium block">Cancellation ID</span>
                        <span className="text-sm font-mono font-semibold text-gray-900">{ticket.cancellationId}</span>
                      </div>
                      <button
                        onClick={() => handleCopy(ticket.cancellationId, 'Cancellation ID')}
                        className="text-gray-400 hover:text-gray-600 p-1.5 rounded hover:bg-white transition-colors"
                        title="Copy Cancellation ID"
                      >
                        <i className={`fas ${copiedField === 'Cancellation ID' ? 'fa-check text-green-600' : 'fa-copy'}`}></i>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>

          {/* ─── Right 1 Column: Conversation & Comments Timeline ─────────── */}
          <div className="lg:col-span-1 space-y-6">

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
              
              {/* Header */}
              <div className="px-5 py-4 border-b border-gray-200 bg-gray-50/50 flex items-center justify-between">
                <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                  <i className="fas fa-comments text-blue-600"></i>
                  Support Discussion
                </h3>
                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                  {commentsList.length}
                </span>
              </div>

              {/* Add Comment Input (if not resolved) */}
              {!isResolved && (
                <div className="p-5 border-b border-gray-100 bg-white">
                  <form onSubmit={handleAddComment} className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Send a Message
                      </label>
                      <textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Type your message or inquiry..."
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none transition-shadow"
                        disabled={addingComment}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={addingComment || !newComment.trim()}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {addingComment ? (
                        <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> Sending...</>
                      ) : (
                        <><i className="fas fa-paper-plane"></i> Send Reply</>
                      )}
                    </button>
                  </form>
                </div>
              )}

              {/* Comments Timeline */}
              <div className="p-5 max-h-[500px] overflow-y-auto space-y-4">
                {commentsList.length > 0 ? (
                  commentsList.map((c, idx) => {
                    const text = getCommentText(c);
                    const author = getCommentAuthor(c);
                    const dateStr = formatDate(getCommentDate(c));

                    return (
                      <div key={idx} className="bg-gray-50 hover:bg-gray-100/70 transition-colors rounded-xl p-3.5 border border-gray-200/80">
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                              {author.charAt(0).toUpperCase()}
                            </div>
                            <span className="text-xs font-bold text-gray-900">{author}</span>
                          </div>
                          {dateStr && (
                            <span className="text-[11px] text-gray-400 whitespace-nowrap">{dateStr}</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed pl-8">
                          {text || '(Empty comment)'}
                        </p>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-10 px-4">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3 text-gray-400">
                      <i className="far fa-comments text-xl"></i>
                    </div>
                    <p className="text-sm font-medium text-gray-700">No replies yet</p>
                    <p className="text-xs text-gray-500 mt-1">Our support team will respond to your ticket soon.</p>
                  </div>
                )}
              </div>

            </div>

          </div>

        </div>

      </div>
    </div>
  );
};

export default SupportTicketDetail;